import { validate as validateUuid } from 'uuid';
import {
	ChatRef,
	DesignSystem,
	DesignSystemVersion,
	DsMode,
	SceneComment,
} from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	clearActiveScene,
	setSceneLoadError,
	updateSceneChatRefs,
	updateSceneComments,
	resolveOptimisticallyUpdatedSceneComments,
	updateActiveSceneDesignSystem,
	updateActiveSceneLinkedDesignSystemVersion,
	updateActiveSceneLatestDesignSystemVersion,
	updateActiveSceneDesignSystemModes,
} from '../redux/db/actions';
import { showMigrationModal } from '../redux/mode/actions';
import { displayMigrationRequiredModal } from '../redux/mode/util';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

interface ScenePollResponse {
	chatRefs: ChatRef[];
	comments?: SceneComment[];
	designSystem: DesignSystem | null;
	dsModes?: DsMode[];
	linkedDesignSystemVersion: DesignSystemVersion | null;
	latestDesignSystemVersion: DesignSystemVersion | null;
	linkedDsIsLegacy: boolean;
	timestamp: string;
}

export class ScenePollingStream extends PollingStream<ScenePollResponse> {
	constructor(activeSceneId: string) {
		// Validate UUIDs before constructing URL
		if (!validateUuid(activeSceneId)) {
			throw new Error(`Invalid scene ID: ${activeSceneId}`);
		}

		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(clearActiveScene());
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chats');
	}

	extractData(response: any): ScenePollResponse[] {
		return [response];
	}

	onData(responses: ScenePollResponse[]) {
		const response = responses.length > 0 ? responses[0] : null;
		if (!response) {
			store.dispatch(clearActiveScene());
			return;
		}

		// Update chat refs
		const processedChatRefs: ChatRef[] = (response.chatRefs || []).map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateSceneChatRefs(processedChatRefs));

		// Update scene comments
		const processedComments: SceneComment[] = (response.comments || []).map((c) => ({
			...c,
			createdAt: new Date(c.createdAt),
			updatedAt: c.updatedAt ? new Date(c.updatedAt) : undefined,
			deletedAt: c.deletedAt ? new Date(c.deletedAt) : null,
		}));
		const commentWriteIds = processedComments
			.map((c) => c.writeId)
			.filter((writeId): writeId is string => !!writeId);
		if (commentWriteIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedSceneComments(commentWriteIds));
		}
		store.dispatch(updateSceneComments(processedComments));

		// Update design system
		if (response.designSystem) {
			const processedDs: DesignSystem = {
				...response.designSystem,
				createdAt: new Date(response.designSystem.createdAt),
				updatedAt: new Date(response.designSystem.updatedAt),
			};
			store.dispatch(updateActiveSceneDesignSystem(processedDs));
		} else {
			store.dispatch(updateActiveSceneDesignSystem(null));
		}

		// Update modes (from linked DS config/display.json)
		if (response.dsModes && response.dsModes.length > 0) {
			store.dispatch(updateActiveSceneDesignSystemModes(response.dsModes));
		} else {
			store.dispatch(updateActiveSceneDesignSystemModes(null));
		}

		// Update linked design system version
		if (response.linkedDesignSystemVersion) {
			const processedVersion: DesignSystemVersion = {
				...response.linkedDesignSystemVersion,
				createdAt: new Date(response.linkedDesignSystemVersion.createdAt),
			};
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(processedVersion));
		} else {
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
		}

		// Update latest design system version
		if (response.latestDesignSystemVersion) {
			const processedVersion: DesignSystemVersion = {
				...response.latestDesignSystemVersion,
				createdAt: new Date(response.latestDesignSystemVersion.createdAt),
			};
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(processedVersion));
		} else {
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
		}

		// Handle legacy linked DS - trigger migration modal with scene context (only if not already showing)
		if (response.linkedDsIsLegacy && response.designSystem) {
			const state = store.getState();
			if (state.mode.migrationModal?.dsId !== response.designSystem.id) {
				const activeSceneId = state.mode.activeSceneId;
				displayMigrationRequiredModal(
					response.designSystem.id,
					'scene',
					activeSceneId || undefined
				);
			}
		} else if (response.designSystem) {
			// Migration completed - close modal if it was open for this DS
			const state = store.getState();
			if (state.mode.migrationModal?.dsId === response.designSystem.id) {
				store.dispatch(showMigrationModal(null));
			}
		}
	}
}
